import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle, ShieldCheck, Target, Layers, Globe, ArrowRight } from "lucide-react";
import BrandLogo from "../components/brand/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const certColor = {
  Gold:   { bg: "bg-amber-300/15",   text: "text-amber-300",   border: "border-amber-300" },
  Silver: { bg: "bg-slate-300/15",   text: "text-slate-200",   border: "border-slate-300" },
  Bronze: { bg: "bg-orange-300/15",  text: "text-orange-300",  border: "border-orange-300" },
  Watch:  { bg: "bg-red-400/15",     text: "text-red-300",     border: "border-red-400" },
};

const PillarRing = ({ label, value }) => {
  const v = Number(value || 0);
  const C = 2 * Math.PI * 38;
  const dash = ((v / 100) * C).toFixed(1);
  return (
    <div className="text-center" data-testid={`scorecard-pillar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="relative w-[110px] h-[110px] mx-auto">
        <svg width="110" height="110" viewBox="0 0 110 110" className="-rotate-90">
          <circle cx="55" cy="55" r="38" stroke="rgba(63,169,245,.12)" strokeWidth="9" fill="none" />
          <circle cx="55" cy="55" r="38" stroke="#3FA9F5" strokeWidth="9" fill="none"
                  strokeDasharray={`${dash} ${C}`} strokeLinecap="round"
                  style={{ transition: "stroke-dasharray 1.2s cubic-bezier(.2,.8,.2,1)" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-display text-2xl text-electric">{v.toFixed(0)}</span>
        </div>
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-cream-dim">{label}</div>
    </div>
  );
};

const PublicScorecard = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/auditiq/scorecard/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Scorecard not found"))
      .finally(() => setLoading(false));
  }, [token]);

  // Update OG meta tags for social sharing once data arrives
  useEffect(() => {
    if (!data) return;
    const overall = data.five_pillars?.overall_score ?? 0;
    const cert = data.five_pillars?.certification_level || "Watch";
    document.title = `${data.organization_name} · AuditIQ Transparency Scorecard · ${cert} ${overall}/100`;
    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (prop) el.setAttribute("property", name); else el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const desc = `${data.organization_name} scored ${overall}/100 (${cert}) on the AuditIQ Five Pillars · ${data.hawkins?.gravitational_core?.title || ""}`;
    setMeta("description", desc);
    setMeta("og:title", `AuditIQ Transparency Scorecard — ${data.organization_name}`, true);
    setMeta("og:description", desc, true);
    setMeta("og:type", "website", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", `AuditIQ Transparency Scorecard — ${data.organization_name}`);
    setMeta("twitter:description", desc);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-deep text-cream flex items-center justify-center">
        <div className="flex items-center gap-2 text-cream-dim">
          <Loader2 className="animate-spin" size={18} /> Loading transparency scorecard…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-navy-deep text-cream flex items-center justify-center px-6">
        <div className="glass p-8 max-w-md text-center" data-testid="scorecard-error">
          <AlertTriangle size={28} className="text-red-300 mx-auto mb-3" />
          <h1 className="font-display text-2xl mb-2">Scorecard unavailable</h1>
          <p className="text-sm text-cream-dim mb-4">{error || "This scorecard link is not active."}</p>
          <Link to="/" className="text-electric underline-offset-2 hover:underline">Return to AuditIQ home</Link>
        </div>
      </div>
    );
  }

  const cert = data.five_pillars?.certification_level || "Watch";
  const cc = certColor[cert] || certColor.Watch;
  const overall = Number(data.five_pillars?.overall_score || 0);
  const generated = data.generated_at ? new Date(data.generated_at).toLocaleDateString(undefined,
    { year: "numeric", month: "short", day: "numeric" }) : "";
  const core = data.hawkins?.gravitational_core || {};

  return (
    <div className="min-h-screen bg-navy-deep text-cream" data-testid="public-scorecard">
      {/* HEADER */}
      <div className="border-b border-electric/15 bg-navy-soft/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo />
          </Link>
          <span className="text-[10px] uppercase tracking-[0.28em] text-cream-dim">Public Transparency Scorecard</span>
        </div>
      </div>

      {/* HERO */}
      <div className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Scorecard · {data.scan_label}</div>
        <h1 className="font-display text-4xl md:text-5xl text-cream mt-3" data-testid="scorecard-org-name">
          {data.organization_name}
        </h1>
        <div className="text-sm text-cream-dim mt-2 flex items-center gap-2">
          <Globe size={12} /> {data.domain}
          {generated && <span className="text-electric/60">· generated {generated}</span>}
        </div>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Cert pill + overall */}
          <div className={`glass p-6 rounded-md border-2 ${cc.border} ${cc.bg} text-center`} data-testid="scorecard-cert">
            <div className="text-[10px] uppercase tracking-[0.28em] text-cream-dim">AuditIQ Certification</div>
            <div className={`font-display text-5xl mt-2 ${cc.text}`}>{cert}</div>
            <div className="font-display text-2xl mt-1 text-cream">{overall.toFixed(1)} <span className="text-cream-dim text-sm">/ 100</span></div>
            <div className="mt-3 flex items-center justify-center gap-1 text-[11px] text-cream-dim">
              <ShieldCheck size={12} /> Composite of 5 pillars
            </div>
          </div>

          {/* Headline metrics */}
          <div className="glass p-6 rounded-md lg:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-cream-dim mb-4">Headline Metrics</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="font-display text-2xl text-electric">{Number(data.headline_metrics?.risk_score || 0).toFixed(0)}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-cream-dim mt-1">Risk Score</div>
              </div>
              <div>
                <div className="font-display text-2xl text-red-300">{data.headline_metrics?.high_severity_flags || 0}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-cream-dim mt-1">High Flags</div>
              </div>
              <div>
                <div className="font-display text-2xl text-electric">{data.headline_metrics?.exposure_categories || 0}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-cream-dim mt-1">Exposure Areas</div>
              </div>
              <div>
                <div className="font-display text-2xl text-electric">{data.headline_metrics?.departments_observed || 0}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-cream-dim mt-1">Departments</div>
              </div>
            </div>
            <p className="text-sm text-cream mt-5 leading-relaxed" data-testid="scorecard-summary">
              {data.executive_summary}
            </p>
          </div>
        </div>
      </div>

      {/* FIVE PILLARS */}
      <div className="max-w-6xl mx-auto px-6 py-10 border-t border-electric/10">
        <div className="flex items-center gap-2 mb-6">
          <Layers size={16} className="text-electric" />
          <h2 className="font-display text-2xl text-cream">Five Pillars</h2>
          <span className="text-xs text-cream-dim ml-2">scored 0–100, higher is healthier</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <PillarRing label="Financial Transparency" value={data.five_pillars?.financial_transparency} />
          <PillarRing label="Infrastructure Risk"    value={data.five_pillars?.infrastructure_risk} />
          <PillarRing label="Cyber Readiness"        value={data.five_pillars?.cyber_readiness} />
          <PillarRing label="Operational Efficiency" value={data.five_pillars?.operational_efficiency} />
          <PillarRing label="Public Trust"           value={data.five_pillars?.public_trust} />
        </div>
      </div>

      {/* HAWKINS GRAVITATIONAL CORE */}
      {core?.title && (
        <div className="max-w-6xl mx-auto px-6 py-10 border-t border-electric/10">
          <div className="flex items-center gap-2 mb-6">
            <Target size={16} className="text-electric" />
            <h2 className="font-display text-2xl text-cream">Hawkins 3+3+1 · Gravitational Core</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="glass p-6 rounded-md border-2 border-electric lg:col-span-2" data-testid="scorecard-hawkins-core">
              <div className="text-[10px] uppercase tracking-[0.28em] text-cream-dim">The single risk pulling the system inward</div>
              <h3 className="font-display text-3xl text-electric mt-2">{core.title}</h3>
              <p className="text-sm text-cream mt-4 leading-relaxed">{core.summary}</p>
              <p className="text-xs text-cream-dim italic mt-3">{core.why_it_matters}</p>
              <div className="mt-4 p-3 rounded bg-electric/10 border-l-2 border-electric">
                <div className="text-[10px] uppercase tracking-[0.2em] text-electric mb-1">First move</div>
                <div className="text-sm text-cream">{core.first_move}</div>
              </div>
            </div>
            <div className="glass p-6 rounded-md">
              <div className="text-[10px] uppercase tracking-[0.28em] text-cream-dim mb-3">Escape path</div>
              <ol className="text-sm text-cream space-y-3 list-decimal pl-4">
                {(data.hawkins?.escape_path || []).map((s, i) => (
                  <li key={i} className="leading-relaxed">{s}</li>
                ))}
              </ol>
              <div className="mt-5 pt-4 border-t border-electric/15 text-[11px] text-cream-dim">
                {data.hawkins?.visible_count} visible · {data.hawkins?.hidden_count} hidden risks identified.
                Full risk register available to authorized reviewers.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RECOMMENDED DECISIONS */}
      {(data.recommended_decisions || []).length > 0 && (
        <div className="max-w-6xl mx-auto px-6 py-10 border-t border-electric/10">
          <div className="flex items-center gap-2 mb-6">
            <ArrowRight size={16} className="text-electric" />
            <h2 className="font-display text-2xl text-cream">Recommended Path Forward</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.recommended_decisions.map((d, i) => (
              <div key={i} className="glass p-4 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-cream-dim">{d.domain}</div>
                  <span className="text-xs text-electric font-mono">{Number(d.domain_score || 0).toFixed(0)}</span>
                </div>
                <div className="text-sm text-cream font-medium">{d.recommended_title}</div>
                <div className="text-[11px] text-cream-dim mt-2">Recommended path · Option {d.recommended_option} ({d.recommended_kind})</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA + DISCLAIMER */}
      <div className="max-w-6xl mx-auto px-6 py-12 border-t border-electric/10">
        <div className="glass p-8 rounded-md text-center">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Powered by AuditIQ</div>
          <h3 className="font-display text-2xl mt-2">Run a transparency scorecard on your organization</h3>
          <p className="text-sm text-cream-dim mt-2 max-w-2xl mx-auto">
            AuditIQ generates Five Pillar scorecards, Hawkins risk analysis, and decision options
            for cities, school districts, and nonprofits — entirely from public information.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-md bg-electric text-navy-deep font-semibold hover:bg-electric/90"
                data-testid="scorecard-cta-contact">
            Request your scorecard <ArrowRight size={14} />
          </Link>
        </div>
        <p className="text-[11px] text-cream-dim italic text-center mt-6">
          {data.disclaimer}
        </p>
      </div>
    </div>
  );
};

export default PublicScorecard;
